import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { Avatar, TierBadge, SkeletonHero, ErrorMessage, StubNote, usePageMeta } from '../components/ui';
import AdSlot from '../components/AdSlot';
import { deriveTier, brawlerMeta, pct, titleCase, brawlerImage } from '../data/meta';

function BrawlerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [brawler, setBrawler] = useState(null);
  const [rank, setRank] = useState(null);
  const [matchups, setMatchups] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const metaName = brawler ? titleCase(brawler.brawler_name) : null;
  usePageMeta({
    title: metaName ? `${metaName} Stats, Best Maps & Counters` : 'Brawlers',
    description: metaName
      ? `${metaName} has a ${pct(brawler.win_rate)} win rate across ${Number(brawler.total_battles || 0).toLocaleString('en-US')} tracked battles. See ${metaName}'s best maps, counters and teammates.`
      : undefined,
    image: brawler ? brawlerImage(brawler.brawler_name) : undefined,
  });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [b, all, mu] = await Promise.all([
          api.getBrawler(id),
          api.getBrawlers('win_rate'),
          api.getBrawlerMatchups(id).catch(() => null),
        ]);
        if (cancelled) return;
        setBrawler(b);
        setMatchups(mu);
        const ranked = all.filter((x) => x.win_rate != null).sort((a, c) => c.win_rate - a.win_rate);
        const idx = ranked.findIndex((x) => String(x.brawler_id) === String(id));
        setRank(idx >= 0 ? idx + 1 : null);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <div className="page"><div className="wrap"><SkeletonHero /></div></div>;
  if (error) return <div className="page"><div className="wrap"><ErrorMessage message={error} /></div></div>;
  if (!brawler) return <div className="page"><div className="wrap"><ErrorMessage message="Brawler not found" /></div></div>;

  const name = brawler.brawler_name;
  const tier = deriveTier(brawler.win_rate);
  const meta = brawlerMeta(name);
  const maps = (brawler.best_maps || []).slice(0, 5);
  const bestMode = maps[0]?.mode;
  const top = maps[0];

  const counters = matchups?.countered_by || [];
  const strongVs = matchups?.strong_against || [];
  const teammates = matchups?.best_teammates || [];

  return (
    <div className="page">
      <div className="wrap">
        <span className="back" onClick={() => navigate('/brawlers')}>← Tier List</span>

        <div className="bdhero">
          <div className="glow" />
          <Avatar name={name} />
          <div className="who">
            <h1>{name} <TierBadge tier={tier} /></h1>
            <div className="tags">
              {meta && <span className="pill-chip">{meta.role}</span>}
              {meta && <span className="pill-chip">{meta.rarity}</span>}
              {bestMode && <span className="pill-chip lime">Best in {bestMode}</span>}
            </div>
          </div>
          <div className="stats">
            <div className="stat"><div className="v">{pct(brawler.win_rate)}</div><div className="l">Win rate</div></div>
            <div className="stat"><div className="v">{pct(brawler.pick_rate)}</div><div className="l">Pick rate</div></div>
            <div className="stat"><div className="v">{rank ? `#${rank}` : '—'}</div><div className="l">Global rank</div></div>
          </div>
        </div>

        <div className="bdcols">
          {/* HOW TO BEAT — real head-to-head counters */}
          <div className="panel">
            <h3><span className="ic beat">⚔</span>How to beat {name}</h3>
            {counters.length > 0 ? (
              <>
                <div className="subhead">Brawlers that counter it</div>
                {counters.map((c) => (
                  <div key={c.brawler_id} className="counter-row" onClick={() => navigate(`/brawlers/${c.brawler_id}`)} style={{ cursor: 'pointer' }}>
                    <Avatar name={c.brawler_name} />
                    <span className="nm">{c.brawler_name}</span>
                    <span className="wr">
                      <span className="lab">beats it</span>
                      <span className="v good">{(100 - c.win_rate).toFixed(1)}%</span>
                    </span>
                  </div>
                ))}
                <div className="tip beat">
                  <b>Tactic:</b> Across tracked battles, {name} loses most to {counters[0].brawler_name}
                  {' '}({(100 - counters[0].win_rate).toFixed(1)}% over {counters[0].total_battles.toLocaleString()} games).
                  Respect that matchup and trade into it only with an advantage.
                </div>
              </>
            ) : (
              <StubNote>Not enough head-to-head battles against {name} yet to rank its counters.</StubNote>
            )}
          </div>

          {/* HOW TO WIN WITH — real best maps + teammate synergy */}
          <div className="panel">
            <h3><span className="ic win">✓</span>How to win with {name}</h3>

            {(brawler.top_star_power || brawler.top_gadget || (brawler.top_gears || []).length > 0) && (
              <>
                <div className="subhead">Popular build · most equipped</div>
                <div className="build-chips">
                  {brawler.top_star_power && <span className="pill-chip lime">★ {brawler.top_star_power}</span>}
                  {brawler.top_gadget && <span className="pill-chip lime">⚙ {brawler.top_gadget}</span>}
                  {(brawler.top_gears || []).map((gear) => (
                    <span key={gear} className="pill-chip lime">🔧 {gear}</span>
                  ))}
                </div>
              </>
            )}

            <div className="subhead">Best maps</div>
            {maps.length > 0 ? (
              <div className="map-list">
                {maps.map((m, i) => (
                  <div key={i} className="map-row" onClick={() => navigate(`/maps/${encodeURIComponent(m.map)}`)}>
                    <span className="mr-name">{m.map} <small>· {m.mode}</small></span>
                    <span className="v">{pct(m.win_rate)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <StubNote>Not enough per-map battles for {name} yet.</StubNote>
            )}

            {teammates.length > 0 && (
              <>
                <div className="subhead">Best teammates</div>
                <div className="mate-row">
                  {teammates.map((m) => (
                    <div key={m.brawler_id} className="mate" onClick={() => navigate(`/brawlers/${m.brawler_id}`)} style={{ cursor: 'pointer' }}>
                      <Avatar name={m.brawler_name} />
                      <span className="nm">{m.brawler_name}</span>
                      <span className="trend up" style={{ fontSize: 11 }}>{pct(m.win_rate)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {top && (
              <div className="tip">
                <b>Win-more:</b> {name} performs best on <b>{top.map}</b> ({top.mode})
                at a {pct(top.win_rate)} win rate over {(top.total_battles ?? 0).toLocaleString()} battles —
                prioritise it when the rotation allows.
              </div>
            )}
          </div>
        </div>

        {/* STRONG AGAINST — real favorable matchups */}
        {strongVs.length > 0 && (
          <div className="panel" style={{ marginTop: 18 }}>
            <h3><span className="ic win">↑</span>{name} is strong against</h3>
            <div className="mate-row" style={{ gap: 16 }}>
              {strongVs.map((c) => (
                <div key={c.brawler_id} className="mate" onClick={() => navigate(`/brawlers/${c.brawler_id}`)} style={{ cursor: 'pointer', width: 70 }}>
                  <Avatar name={c.brawler_name} size={52} />
                  <span className="nm">{c.brawler_name}</span>
                  <span className="trend up" style={{ fontSize: 12 }}>{pct(c.win_rate)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <AdSlot />

        <div className="panel" style={{ marginTop: 18 }}>
          <h3><span className="ic win">↑</span>Performance breakdown</h3>
          <div className="stat-ribbon" style={{ marginBottom: 0, gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="srib"><div className="v">{(brawler.total_battles ?? 0).toLocaleString()}</div><div className="l">Total battles</div></div>
            <div className="srib"><div className="v">{(brawler.wins ?? 0).toLocaleString()}</div><div className="l">Wins</div></div>
            <div className="srib"><div className="v">{(brawler.losses ?? 0).toLocaleString()}</div><div className="l">Losses</div></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BrawlerDetail;
